import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  LANDING_COLUMN_WIDTH2,
  LANDING_COLUMN_WIDTH2_MOBILE,
  LANDING_COLUMN_GAP2,
  type LandingArchiveCandidate,
  type LandingArchiveGroup,
} from '../../shared/util/landingArchiveIndex';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DeviceType } from '../../shared/enum/EnumRepository';

export interface LandingArchiveIndexProps {
  groups: LandingArchiveGroup[];
  onCandidateActivate: (candidate: LandingArchiveCandidate) => void;
  onCandidatePreviewChange: (candidate: LandingArchiveCandidate | null) => void;
}

const LandingArchiveIndex: React.FC<LandingArchiveIndexProps> = ({
  groups,
  onCandidateActivate,
  onCandidatePreviewChange,
}) => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [mobileColumns, setMobileColumns] = useState<LandingArchiveGroup[][]>([groups]);

  useLayoutEffect(() => {
    if (!isMobile) return;

    const container = containerRef.current;
    const measurement = measurementRef.current;
    if (!container || !measurement) return;

    const measure = () => {
      const maxHeight = container.clientHeight;
      if (maxHeight <= 0) return;

      const sections = Array.from(measurement.children) as HTMLElement[];
      const columns: LandingArchiveGroup[][] = [[]];
      let usedHeight = 0;

      const nextColumn = () => {
        columns.push([]);
        usedHeight = 0;
      };

      groups.forEach((group, index) => {
        const section = sections[index];
        if (!section) return;

        const sectionHeight = section.getBoundingClientRect().height;
        if (usedHeight > 0 && usedHeight + sectionHeight > maxHeight) nextColumn();

        if (sectionHeight <= maxHeight) {
          columns[columns.length - 1].push(group);
          usedHeight += sectionHeight;
          return;
        }

        // A single group can be taller than the screen when text is enlarged.
        const children = Array.from(section.children) as HTMLElement[];
        const labelHeight = group.label ? children[0].getBoundingClientRect().height : 0;
        const itemHeights = children.slice(group.label ? 1 : 0).map((child) =>
          child.getBoundingClientRect().height
        );
        const padding = parseFloat(getComputedStyle(section).paddingBottom) || 0;
        let part: LandingArchiveGroup = { ...group, candidates: [] };
        let partHeight = labelHeight + padding;

        group.candidates.forEach((candidate, candidateIndex) => {
          const itemHeight = itemHeights[candidateIndex] ?? 0;
          if (part.candidates.length > 0 && usedHeight + partHeight + itemHeight > maxHeight) {
            columns[columns.length - 1].push(part);
            nextColumn();
            part = { ...group, label: '', candidates: [] };
            partHeight = padding;
          }
          part.candidates.push(candidate);
          partHeight += itemHeight;
        });

        columns[columns.length - 1].push(part);
        usedHeight += partHeight;
      });

      setMobileColumns(columns);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(measurement);
    window.visualViewport?.addEventListener('resize', measure);
    let active = true;
    document.fonts?.ready.then(() => {
      if (active) measure();
    });

    return () => {
      active = false;
      observer.disconnect();
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [groups, isMobile]);

  const handleCandidateClick = useCallback(
    (e: React.MouseEvent, candidate: LandingArchiveCandidate) => {
      e.stopPropagation();
      onCandidateActivate(candidate);
    },
    [onCandidateActivate]
  );

  const renderCandidateItem = (
    candidate: LandingArchiveCandidate,
    key: string
  ) => (
    <CandidateItem
      key={key}
      $isMobile={isMobile}
      $isInteractive={Boolean(candidate.aui?.trim())}
      onClick={(e) => handleCandidateClick(e, candidate)}
      role={candidate.aui?.trim() ? 'button' : 'presentation'}
      tabIndex={candidate.aui?.trim() ? 0 : -1}
      onFocus={() => onCandidatePreviewChange(candidate)}
      onBlur={() => onCandidatePreviewChange(null)}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          onCandidatePreviewChange(candidate);
        }
      }}
      onPointerLeave={() => onCandidatePreviewChange(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onCandidateActivate(candidate);
        }
      }}
    >
      <CandidateUsername $isMobile={isMobile}>{candidate.username}</CandidateUsername>
    </CandidateItem>
  );

  const renderGroup = (group: LandingArchiveGroup) => (
    <GroupSection key={group.letter}>
      {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
      {group.candidates.map((candidate, index) =>
        renderCandidateItem(candidate, `${candidate.handle}-${candidate.username}-${index}`)
      )}
    </GroupSection>
  );

  return (
    <CandidateContainer ref={containerRef} data-mobile={isMobile ? 'true' : 'false'}>
      {isMobile && (
        <MeasurementColumn ref={measurementRef} aria-hidden="true">
          {groups.map((group) => (
            <GroupSection key={group.letter}>
              {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
              {group.candidates.map((candidate, index) => (
                <CandidateItem key={index} $isMobile={true} $isInteractive={false}>
                  <CandidateUsername $isMobile={true}>{candidate.username}</CandidateUsername>
                </CandidateItem>
              ))}
            </GroupSection>
          ))}
        </MeasurementColumn>
      )}
      <ColumnList $isMobile={isMobile}>
        {isMobile
          ? mobileColumns.map((column, columnIndex) => (
              <MobileColumn key={columnIndex}>
                {column.map(renderGroup)}
              </MobileColumn>
            ))
          : groups.map(renderGroup)}
      </ColumnList>
    </CandidateContainer>
  );
};

const CandidateContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ColumnList = styled.div<{ $isMobile: boolean }>`
  height: 100%;
  width: ${({ $isMobile }) => $isMobile ? 'max-content' : LANDING_COLUMN_WIDTH2};
  flex-shrink: 0;
  ${({ $isMobile }) => $isMobile ? `
    display: flex;
    align-items: flex-start;
    gap: ${LANDING_COLUMN_GAP2};
  ` : `
    column-count: 1;
    column-gap: ${LANDING_COLUMN_GAP2};
    column-fill: auto;
  `}
`;

const MobileColumn = styled.div`
  width: ${LANDING_COLUMN_WIDTH2_MOBILE};
  flex-shrink: 0;
`;

const MeasurementColumn = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${LANDING_COLUMN_WIDTH2_MOBILE};
  visibility: hidden;
  pointer-events: none;
`;

const GroupSection = styled.section`
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
  break-inside: avoid;
`;

const GroupLabel = styled.div`
  width: 100%;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  line-height: 1;
  break-after: avoid;
`;

const CandidateItem = styled.div<{ $isMobile: boolean; $isInteractive: boolean }>`
  width: 100%;
  min-width: ${({ $isMobile }) => ($isMobile ? 0 : 'auto')};
  display: flex;
  align-items: center;
  break-inside: avoid;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'pointer' : 'default')};

  &:hover > div {
    transform: scale(0.95);
  }
`;

const CandidateUsername = styled.div<{ $isMobile: boolean }>`
  padding: 3px 0;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  transform-origin: left center;
  transition: transform 0.25s ease-in-out;
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `
      : `
    width: clamp(100px, 50%, 180px);
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default LandingArchiveIndex;
